import * as Styles from './styles';

import { useContext, useEffect, useState } from 'react';
import { Input } from '@acko-tech/building-blocks.ui.input';
import { MPosContext } from 'utils/context';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';

// components
import DetailsCard from './DetailsCard';

// utils
import { isValidVinNumber } from './utils';
import {
    SegmentPages,
    SegmentTrackEvents,
    SegmentEventNameTypes,
    SegmentEventButtonNames,
    SegmentEventActionTypes,
    SegmentEventActionNames,
    PRODUCT,
    SUB_PRODUCT_NAME,
    SegmentEventFieldTypes,
} from 'utils/segment/constants';
import {
    AssetDetails,
    validateVinNumber,
    vinDetailsResponse,
    CustomerDetails,
    ModelDetails,
} from './api';
import SnackBar from 'library/snackbar';
import { ImeiScannerModal } from 'features/imeiScanner';
import { getFieldActionName } from '../../customerDetails/helpers';

// constants
import {
    SERVICE_UNAVAILABLE_ERROR_CODE,
    VALIDATION_SERVICE_UNAVAILABLE_ERROR_CODE,
    DETAILS_NOT_FOUND_ERROR_CODE,
    getErrorMessage,
    getManualFlowMessage,
} from './constants';
import { NewPlanStages } from '../../constants';
import sendSegmentEvent from 'utils/segment';
import { ERROR_MESSAGES, Permissions } from 'utils/constants';
import {
    areRequiredEditableFieldsComplete,
    EditableFieldKey,
    hasEditableFieldErrors,
    validateEditableFields,
    validateField,
} from './validators';

const INITIAL_FIELD_ERRORS: Record<EditableFieldKey, string> = {
    phone: '',
    email: '',
    address: '',
    pincode: '',
    rto_code: '',
    charger_number: '',
};

const getEmptyVehicleDetails = (): AssetDetails => ({
    asset_number: '',
    name: '',
    purchase_date: '',
    motor_number: '',
    charger_number: '',
    bin: '',
    bin2: '',
    rto_code: '',
    registration_number: '',
});

const getEmptyCustomerDetails = (): CustomerDetails => ({
    name: '',
    phone: '',
    email: '',
    address: '',
    pincode: '',
});

const VehicleValidationScreen = ({
    setSelectedModelOption,
}: {
    setSelectedModelOption: (option: {
        displayValue: string;
        value: string;
    }) => void;
}) => {
    const {
        partnerId,
        dealerType,
        setPlanDetails,
        planDetails,
        setActiveStage,
        userDetails,
        permissions,
        permissionsMap,
        metaData,
    } = useContext(MPosContext);

    const { isMobile } = useResponsiveDevice();

    const [onClickVinNumber, setOnClickVinNumber] = useState(false);
    const [isVinValidateDisabled, setIsVinValidateDisabled] = useState(true);
    const [isDetailsContinueDisabled, setIsDetailsContinueDisabled] =
        useState(true);
    const [vin, setVin] = useState('');
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [cameraSupported, setCameraSupported] = useState(false);
    const [buttonLoaders, setButtonLoaders] = useState({
        isVinNumberValidationLoading: false,
        submitButtonLoaders: false,
    });
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [modelDetails, setModelDetails] = useState<ModelDetails>({
        description: '',
        id: '',
        name: '',
        price: 0,
        type: '',
        brand: '',
    });

    const [isVinNumberVerified, setIsVinNumberVerified] = useState(false);
    const [vehicleDetails, setVehicleDetails] = useState<AssetDetails>({
        asset_number: '',
        name: '',
        purchase_date: '',
        motor_number: '',
        charger_number: '',
        bin: '',
        bin2: '',
        rto_code: '',
        registration_number: '',
    });
    const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
        name: '',
        phone: '',
        email: '',
        address: '',
        pincode: '',
    });
    const [editingFields, setEditingFields] = useState<Set<EditableFieldKey>>(
        new Set()
    );
    const [fieldErrors, setFieldErrors] =
        useState<Record<EditableFieldKey, string>>(INITIAL_FIELD_ERRORS);
    const { group } = permissions;

    useEffect(() => {
        if (onClickVinNumber) {
            trackEvent(SegmentEventNameTypes.ACTION, {
                page_url: window?.location?.href || '',
                button_name: SegmentEventButtonNames.CLICK_VIN_NUMBER,
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.CLICK_VIN_NUMBER,
                product: PRODUCT,
                sub_product: SUB_PRODUCT_NAME,
                platform: isMobile ? 'mweb' : 'web',
                device: navigator.userAgent || '',
                partner: partnerId,
                field_selected: SegmentEventFieldTypes.TEXT,
                plan_id: planDetails.plan.plan_id,
                device_model: planDetails.device.name,
                category: planDetails.device.type,
            });
        }
    }, [onClickVinNumber]); // we can remove this check

    const [validationErr, setValidationErr] = useState({
        vin_number: '',
    });

    const resetEditableFieldState = () => {
        setEditingFields(new Set());
        setFieldErrors(INITIAL_FIELD_ERRORS);
        setIsDetailsContinueDisabled(true);
    };

    const updateDetailsContinueState = (
        nextCustomerDetails: CustomerDetails,
        nextVehicleDetails: AssetDetails
    ) => {
        const errors = validateEditableFields(
            nextCustomerDetails,
            nextVehicleDetails
        );
        setFieldErrors(errors);
        setIsDetailsContinueDisabled(
            !areRequiredEditableFieldsComplete(
                nextCustomerDetails,
                nextVehicleDetails
            ) || hasEditableFieldErrors(errors)
        );
    };

    const handleCustomerFieldChange = (
        field: keyof CustomerDetails,
        value: string
    ) => {
        const nextCustomerDetails = {
            ...customerDetails,
            [field]: value,
        };
        setCustomerDetails(nextCustomerDetails);
        const fieldKey = field as EditableFieldKey;
        const fieldError = validateField(fieldKey, value);
        const nextErrors = {
            ...fieldErrors,
            [fieldKey]: fieldError,
        };
        setFieldErrors(nextErrors);
        setIsDetailsContinueDisabled(
            !areRequiredEditableFieldsComplete(
                nextCustomerDetails,
                vehicleDetails
            ) || hasEditableFieldErrors(nextErrors)
        );
    };

    const handleVehicleFieldChange = (
        field: 'rto_code' | 'charger_number',
        value: string
    ) => {
        const nextVehicleDetails = {
            ...vehicleDetails,
            [field]: value,
        };
        setVehicleDetails(nextVehicleDetails);
        const fieldError = validateField(field, value);
        const nextErrors = {
            ...fieldErrors,
            [field]: fieldError,
        };
        setFieldErrors(nextErrors);
        setIsDetailsContinueDisabled(
            !areRequiredEditableFieldsComplete(
                customerDetails,
                nextVehicleDetails
            ) || hasEditableFieldErrors(nextErrors)
        );
    };

    const handleEditField = (field: EditableFieldKey) => {
        setEditingFields((currentFields) => new Set(currentFields).add(field));
    };

    const getEditableFieldValue = (field: EditableFieldKey): string => {
        if (field === 'rto_code' || field === 'charger_number') {
            return vehicleDetails[field] || '';
        }
        return customerDetails[field] || '';
    };

    const handleSaveField = (field: EditableFieldKey) => {
        const fieldValue = getEditableFieldValue(field);
        const fieldError = validateField(field, fieldValue);
        const nextErrors = {
            ...fieldErrors,
            [field]: fieldError,
        };

        setFieldErrors(nextErrors);
        setIsDetailsContinueDisabled(
            !areRequiredEditableFieldsComplete(
                customerDetails,
                vehicleDetails
            ) || hasEditableFieldErrors(nextErrors)
        );

        if (fieldError.length > 0) {
            return;
        }

        setEditingFields((currentFields) => {
            const nextFields = new Set(currentFields);
            nextFields.delete(field);
            return nextFields;
        });
    };

    const getEditableFieldActionName = (field: EditableFieldKey) => {
        if (field === 'phone') {
            return getFieldActionName('mobile');
        }
        return getFieldActionName(field);
    };

    const handleEditableFieldFocus = (field: EditableFieldKey) => {
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.ENTER_TEXT,
                action_name: getEditableFieldActionName(field),
            },
            { userDetails, isMobile }
        );
    };

    const validateVinHandler = async () => {
        // segment events
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name:
                    SegmentEventActionNames.CLICK_VALIDATE_VIN_NUMBER_BUTTON,
            },
            { userDetails, isMobile }
        );
        setButtonLoaders((buttonLoaders) => ({
            ...buttonLoaders,
            isVinNumberValidationLoading: true,
        }));
        setVin(vin);
        setPlanDetails({
            ...planDetails,
            device: {},
            customer: {},
            showManualFlow: false,
        });
        setSelectedModelOption({ displayValue: '', value: '' });
        resetEditableFieldState();
        setIsVinNumberVerified(false);
        setVehicleDetails(getEmptyVehicleDetails());
        setCustomerDetails(getEmptyCustomerDetails());

        try {
            const vinNumber = vin.trim();
            const response: vinDetailsResponse = await validateVinNumber(
                partnerId,
                vinNumber,
                group
            );
            if (response.isValid) {
                setIsVinNumberVerified(true);
                const nextVehicleDetails =
                    response?.assetDetails ?? getEmptyVehicleDetails();
                const nextCustomerDetails =
                    response?.customerDetails ?? getEmptyCustomerDetails();
                const nextModelDetails = response?.modelDetails;
                setVehicleDetails(nextVehicleDetails);
                setCustomerDetails(nextCustomerDetails);
                if (nextModelDetails) {
                    setModelDetails(nextModelDetails);
                }
                updateDetailsContinueState(
                    nextCustomerDetails,
                    nextVehicleDetails
                );
                setSnackbar({
                    type: 'success',
                    message: response?.message,
                });
                setPlanDetails({
                    ...planDetails,
                    showManualFlow: false,
                });
            } else {
                if (
                    response?.errorCode ===
                        VALIDATION_SERVICE_UNAVAILABLE_ERROR_CODE ||
                    SERVICE_UNAVAILABLE_ERROR_CODE ||
                    DETAILS_NOT_FOUND_ERROR_CODE
                ) {
                    const errorMessage = getErrorMessage(response?.errorCode);
                    // segment events
                    sendSegmentEvent(
                        SegmentEventNameTypes.EXCEPTION,
                        {
                            message: errorMessage,
                        },
                        { userDetails, isMobile }
                    );
                    setPlanDetails({
                        ...planDetails,
                        showManualFlow: true,
                    });
                }
                setIsVinNumberVerified(false);
                setValidationErr((validationErr) => ({
                    ...validationErr,
                    vin_number: response?.message,
                }));
            }
        } catch {
            // segment events
            sendSegmentEvent(
                SegmentEventNameTypes.EXCEPTION,
                {
                    message: ERROR_MESSAGES.VIN_VALIDATION_FAILED_MESSAGE,
                },
                { userDetails, isMobile }
            );
            setPlanDetails({
                ...planDetails,
                showManualFlow: true,
            });
        } finally {
            setButtonLoaders((buttonLoaders) => ({
                ...buttonLoaders,
                isVinNumberValidationLoading: false,
            }));
        }
    };

    const onSubmit = async () => {
        setButtonLoaders((buttonLoaders) => ({
            ...buttonLoaders,
            submitButtonLoaders: true,
        }));
        setPlanDetails({
            ...planDetails,
            device: {
                ...planDetails?.device,
                vin_number: vehicleDetails?.asset_number,
                name: modelDetails?.name,
                invoice_date: vehicleDetails?.purchase_date,
                motor_number: vehicleDetails?.motor_number,
                charger_number: vehicleDetails?.charger_number,
                bin_number: vehicleDetails?.bin,
                bin2_number: vehicleDetails?.bin2,
                rto_code: vehicleDetails?.rto_code,
                registration_number: vehicleDetails?.registration_number,
                type: modelDetails?.type,
                price: modelDetails?.price,
                id: modelDetails?.id,
                description: modelDetails?.description,
                brand: modelDetails?.brand,
            },
            customer: {
                ...planDetails?.customer,
                name: customerDetails?.name?.trim(),
                phone: customerDetails?.phone?.trim(),
                email: customerDetails?.email?.trim(),
                address: customerDetails?.address?.trim(),
                pincode: customerDetails?.pincode?.trim(),
            },
        });
        try {
            await trackEvent(
                SegmentTrackEvents.TAP_SUBMIT_SERIAL_NUMBER_MODEL_PAGE,
                {
                    page: SegmentPages.NEW_PLAN_DEVICE_MODELS,
                    partner: partnerId,
                    product: 'pos',
                    journey: 'buy',
                    platform: 'mweb',
                    type: dealerType,
                }
            );
        } catch (e) {
            console.error(e);
        } finally {
            setButtonLoaders((buttonLoaders) => ({
                ...buttonLoaders,
                submitButtonLoaders: false,
            }));
            setActiveStage(NewPlanStages.PLAN);
        }
    };

    const onFocusVinHandler = () => {
        // segment events - should be called when VIN input is focused
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.ENTER_VIN_NUMBER,
                field_type: SegmentEventFieldTypes.TEXT,
            },
            { userDetails, isMobile }
        );
    };

    useEffect(() => {
        setCameraSupported(
            typeof navigator !== 'undefined' &&
                !!navigator.mediaDevices?.getUserMedia
        );
    }, []);

    const handleVinScanned = (scannedVin: string) => {
        setVin(scannedVin);
        if (isValidVinNumber(scannedVin)) {
            setIsVinValidateDisabled(false);
        } else {
            setIsVinValidateDisabled(true);
        }
        setValidationErr((validationErr) => ({
            ...validationErr,
            vin_number: '',
        }));
        setIsScannerOpen(false);
    };

    const onKeyDownVinHandler = (e) => {
        if (e.key === 'Enter' && isValidVinNumber(e.target.value)) {
            e.preventDefault();
            validateVinHandler();
        }
    };

    const onChangeVinHandler = (e) => {
        const updatedVin = e.target.value;
        setVin(updatedVin);
        if (isValidVinNumber(updatedVin)) {
            setIsVinValidateDisabled(false);
        } else {
            setIsVinValidateDisabled(true);
        }
        // resetting validation error here
        setValidationErr((validationErr) => ({
            ...validationErr,
            vin_number: '',
        }));
    };

    const showManualFlow = planDetails?.showManualFlow ?? false;
    const isManualFlowPermissionEnabled =
        permissionsMap[Permissions.MANUAL_PURCHASE_FLOW] ?? false;

    return (
        <>
            <Styles.Wrapper>
                <Styles.VinNumberHeading>
                    Vehicle Validation
                </Styles.VinNumberHeading>
                <Styles.VinNumberText>
                    Enter Vehicle Vin Number to validate and view details
                </Styles.VinNumberText>
                <Styles.InputCard>
                    <Styles.InputWrapper
                        onClick={() => setOnClickVinNumber(true)}
                    >
                        <Styles.EnterMoreDetails>
                            Vehicle VIN Number
                        </Styles.EnterMoreDetails>
                        <Styles.InputAndScanWrapper>
                            <Styles.InputContainer>
                                <Input
                                    className={''}
                                    name={'Vehicle VIN Number'}
                                    placeHolder={'Enter VIN Number'}
                                    type={'text'}
                                    value={vin}
                                    onChange={onChangeVinHandler}
                                    onKeyDown={onKeyDownVinHandler}
                                    onFocus={onFocusVinHandler}
                                    errMsg={validationErr?.vin_number}
                                    hasError={
                                        validationErr?.vin_number?.length > 0
                                    }
                                />
                            </Styles.InputContainer>
                            {permissionsMap[Permissions.SCANNER] &&
                                cameraSupported && (
                                    <Styles.ScanButton
                                        type="button"
                                        onClick={() => setIsScannerOpen(true)}
                                        title="Scan VIN barcode"
                                    >
                                        📷 Scan
                                    </Styles.ScanButton>
                                )}
                        </Styles.InputAndScanWrapper>
                    </Styles.InputWrapper>
                    <Styles.VinNumberText>
                        Find VIN in Vehicle Documents or on the chassis
                    </Styles.VinNumberText>
                    <Styles.ButtonWrapper>
                        <PrimaryButton
                            disabled={isVinValidateDisabled}
                            onClick={validateVinHandler}
                            loading={buttonLoaders.isVinNumberValidationLoading}
                        >
                            Validate device
                        </PrimaryButton>
                    </Styles.ButtonWrapper>
                </Styles.InputCard>
                {showManualFlow && (
                    <Styles.ManualFlowMessage>
                        {getManualFlowMessage(isManualFlowPermissionEnabled)}
                    </Styles.ManualFlowMessage>
                )}
                {!showManualFlow && isVinNumberVerified && (
                    <DetailsCard
                        isButtonDisabled={isDetailsContinueDisabled}
                        onSubmit={onSubmit}
                        isLoading={buttonLoaders.submitButtonLoaders}
                        vehicleDetails={vehicleDetails}
                        customerDetails={customerDetails}
                        modelDetails={modelDetails}
                        editingFields={editingFields}
                        fieldErrors={fieldErrors}
                        onCustomerFieldChange={handleCustomerFieldChange}
                        onVehicleFieldChange={handleVehicleFieldChange}
                        onEditField={handleEditField}
                        onSaveField={handleSaveField}
                        onFieldFocus={handleEditableFieldFocus}
                    />
                )}
            </Styles.Wrapper>
            {permissionsMap[Permissions.SCANNER] && (
                <ImeiScannerModal
                    isOpen={isScannerOpen}
                    onClose={() => setIsScannerOpen(false)}
                    onImeiScanned={handleVinScanned}
                />
            )}
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </>
    );
};

export default VehicleValidationScreen;
